import { CommonModule, NgIf } from '@angular/common'
import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { RouterModule } from '@angular/router'
import { CapitalizePipe } from '../../pipes/capitalize.pipe'
import { BlockUnblockComponent } from '../template/block-unblock/block-unblock.component'
import { DeleteComponent } from '../template/delete/delete.component'
import { PaginationComponent } from '../template/pagination/pagination.component'
import { NgSelectModule } from '@ng-select/ng-select'
import { Observable, Subject, takeUntil } from 'rxjs'
import { Store } from '@ngrx/store'
import { eps } from '../../services/url.service'
import { Business } from '../../store/business/business.state'
import { selectAllBusinesses } from '../../store/business/business.selectors'
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { Actions, ofType } from '@ngrx/effects'
import { UtilsService } from '../../services/utils.service'
import { ToastrService } from 'ngx-toastr'
import { FormatModuleNamePipe } from '../../pipes/format-module-name.pipe'
import { loadBusinesses } from '../../store/business/business.actions'
import { selectPermissionSets } from '../../store/permission-sets/permission-sets.reducer'
import { PermissionSetService } from '../../services/permission-set.service'
import {
  loadPermissionSets,
  createPermissionSet,
  updatePermissionSet,
  createPermissionSetSuccess,
  updatePermissionSetSuccess
} from '../../store/permission-sets/permission-sets.actions'
import {
  PermissionSet,
  PermissionSetPermission
} from '../../store/permission-sets/permission-sets.state'
import { noSpacesValidator } from '../../validators/no-spaces.validators'
import { noLeadingTrailingSpaces } from '../../validators/no-leadingTraillingSpace.validators'
import { TrimInputDirective } from '../../directives/trim-input.directive'
import { HasPermissionDirective } from '../../directives/has-permission.directive'

// interface ApiModule {
//   id: string
//   name: string
//   permissions: { action: string }[]
// }

interface PermissionModule {
  key: string
  name: string
  permissions: string[]
}

interface ApiPermissionPayload {
  moduleId: string
  permissionIds: string[]
}

// api-module.model.ts
export interface ApiPermission {
  id: string
  action: string
  moduleId?: string
}

export interface ApiModule {
  id: string
  name: string
  permissions: ApiPermission[]
  adminOnly?: boolean
  scopeType?: string
}

type PermissionAction = 'status' | 'view' | 'add' | 'edit' | 'delete'

type Permissions = Partial<Record<PermissionAction, boolean>>

@Component({
  selector: 'app-permissionset-permission',
  imports: [
    ReactiveFormsModule,
    NgIf,
    CommonModule,
    RouterModule,
    CapitalizePipe,
    BlockUnblockComponent,
    FormsModule,
    DeleteComponent,
    PaginationComponent,
    NgSelectModule,
    FormatModuleNamePipe,
    TrimInputDirective,
    HasPermissionDirective
  ],
  templateUrl: './permission-set.component.html',
  styleUrl: './permission-set.component.scss'
})
export class PermissionSetComponent implements OnInit {
  private store = inject(Store) // Inject store using `inject()`
  toastr = inject(ToastrService)

  eps = eps
  loading$: Observable<boolean> = this.store.select(state => state.PermissionSet.loading)
  error$: Observable<string | null> = this.store.select(state => state.role.error)
  pageLastRecord = 0
  totalCount$!: Observable<number>
  totalCount = 0
  permissionSets$: Observable<PermissionSet[]> = this.store.select(selectPermissionSets)
  currentPage = 1
  pageLimit = 10
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  private destroyed$ = new Subject<void>()
  readonly systemRoles = ['Admin', 'Business Owner', 'Employee', 'Contractor', 'Trainee']
  search = ''
  isStandard = false
  status = ''
  modules!: ApiModule[]
  permissionModules!: PermissionModule[]
  permissionsState: {
    [key: string]: Partial<Record<PermissionAction, boolean>>
  } = {}
  form!: FormGroup
  @ViewChild('addBusinessModals') addBusinessModals!: TemplateRef<any>
  private modalRef!: NgbModalRef
  modalTitle: any
  blockPermissionSetId = ''
  businessId: string | null = null
  assignedMap: Record<string, boolean> = {}

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private actions$: Actions,
    private utilsService: UtilsService,
    private permissionSetService: PermissionSetService
  ) {
    // this.store.dispatch(resetPermissionSetState())
    this.permissionSets$ = this.store.select(selectPermissionSets)
    this.store.dispatch(loadBusinesses({ params: null }))
  }

  ngOnInit() {
    this.loadPermissionSets()
    this.loadModules()
    // this.totalCount$.subscribe(count => {
    //   this.totalCount = count
    //   this.updatePageLastRecord()
    // })
    this.totalCount = 10

    this.form = this.fb.group({
      businessId: [{ value: null, disabled: false }, Validators.required],
      name: ['', [Validators.required, Validators.maxLength(250), noLeadingTrailingSpaces()]],
      permissions: ['']
    })

    this.actions$
      .pipe(
        ofType(createPermissionSetSuccess, updatePermissionSetSuccess),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.loadPermissionSets()
        this.modalService.dismissAll() // or modalRef.close() if you're using one
        this.form.reset()
        this.blockPermissionSetId = ''
        // console.log('log permission calling')
        this.resetPermissions()
      })
  }

  loadPermissionSets() {
    const params: any = {}
    // params.roleType = 'Role'
    // if (this.businessIdss?.length) {
    //   params.businessIds = this.businessIdss.join(',')
    // }

    // if (this.search) {
    //   params.name = this.search
    // }

    // if (this.status) {
    //   const isDisabled = this.status === 'true' ? true : this.status === 'false' ? false : null
    //   params.disabled = isDisabled
    // }

    // if (this.isStandard) {
    //   params.isStandard = this.isStandard
    // }

    // if (this.businessId) {
    //   params.businessId = this.businessId
    // }

    this.store.dispatch(
      loadPermissionSets({
        params: new URLSearchParams(params).toString(),
        page: this.currentPage,
        limit: this.pageLimit
      })
    )
  }

  // onSearch() {
  //   this.currentPage = 1 // Reset to the first page on search
  //   const params = this.search ? `name=${encodeURIComponent(this.search)}` : null

  //   if (this.status) {
  //     const isDisabled = this.status === 'true' ? true : this.status === 'false' ? false : null
  //     params.disabled = isDisabled
  //   }

  //   this.store.dispatch(
  //     loadPermissionSets({ params, page: this.currentPage, limit: this.pageLimit })
  //   ) // Dispatch action with filter
  // }

  onSearch() {
    this.currentPage = 1 // Reset to the first page on search
    // const params = this.search ? `name=${encodeURIComponent(this.search)}` : null
    const params: any = {}
    if (this.search) {
      params.name = this.search
    }

    if (this.status) {
      const isDisabled = this.status === 'true' ? true : this.status === 'false' ? false : null
      params.disabled = isDisabled
    }

    if (this.isStandard) {
      params.isStandard = this.isStandard
    }

    if (this.businessId) {
      params.businessId = this.businessId
    }
    this.store.dispatch(
      loadPermissionSets({
        params: new URLSearchParams(params).toString(),
        page: this.currentPage,
        limit: this.pageLimit
      })
    ) // Dispatch action with filter
  }

  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      this.loadPermissionSets() // Reload businesses for the new page
      this.updatePageLastRecord()
    }
  }

  private updatePageLastRecord() {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  }

  onPageLimitChange() {
    this.currentPage = 1 // Reset to first page when limit changes
    this.loadPermissionSets() // Load employees with new page limit
  }

  getDashboardRoute(): string {
    // return this.user?.role === 'Admin' ? eps.dashboardPath : eps.employeeDashboardPath
    return this.utilsService.getDashboardRoute()
  }

  loadModules() {
    this.permissionSetService.modules().subscribe({
      next: response => {
        this.modules = response.modules
        this.permissionModules = this.mapApiModules(response.modules)
      },
      error: (error: { message: string }) => {
        this.toastr.error('Error: ' + error.message)
      }
    })
  }

  mapApiModules(apiModules: ApiModule[]): PermissionModule[] {
    return apiModules.map(m => ({
      key: m.name.toLowerCase().replace(/\s+/g, ''), // slugify name for key
      name: m.name,
      permissions: m.permissions.map(p => p.action)
    }))
  }

  getChecked(event: Event): boolean {
    const target = event.target as HTMLInputElement
    return target ? target.checked : false // Default to false if target is null
  }

  togglePermission(module: any, action: string, value: boolean): void {
    const key = module.key
    if (!this.permissionsState[module.key]) {
      this.permissionsState[module.key] = {}
    }
    // Set the selected action
    this.permissionsState[key][action as PermissionAction] = value

    // Auto-check 'view' if 'add', 'edit', or 'delete' is selected
    if ((action === 'add' || action === 'edit' || action === 'delete') && value) {
      this.permissionsState[module.key]['view'] = true
    }
  }

  onClose() {
    // if (this.form) {
    //   this.form.reset(); // Reset the form if it is defined
    // }
    // this.modalService.dismissAll();
    // this.currentStep = 1;

    try {
      this.blockPermissionSetId = ''
      this.resetPermissions()
      if (this.form) {
        this.form.reset()
      }
      if (this.modalRef) {
        this.modalRef.close()
      }
      this.modalService.dismissAll()
    } catch (error) {
      console.error('Error closing modal:', error)
    }
  }
  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    // console.log('control name', control)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null // No error
  }

  onSubmit() {
    const transformedPermissions = this.getTransformedPermissionsForApi()

    if (this.form.invalid) return

    // let roleType = ''
    // if (this.modalTitle === 'Create Role' || this.modalTitle === 'Edit Role') {
    //   roleType = 'Role'
    // }
    // if (
    //   this.modalTitle === 'Create Permission Sets' ||
    //   this.modalTitle === 'Edit Permission Sets'
    // ) {
    //   roleType = 'Permission Sets'
    //   this.isStandard = false
    // }
    if (this.form.get('isStandard')?.value === null || undefined) {
      this.isStandard = false
      this.form.get('isStandard')?.setValue(false)
    }
    if (this.form.valid && !this.blockPermissionSetId) {
      this.store.dispatch(
        createPermissionSet({
          name: this.form.get('name')?.value,
          permissions: transformedPermissions, // ✅ stringify the object
          businessId: this.form.get('businessId')?.value
        })
      )
    } else if (this.form.valid && this.blockPermissionSetId) {
      this.store.dispatch(
        updatePermissionSet({
          id: this.blockPermissionSetId,
          name: this.form.get('name')?.value,
          permissions: transformedPermissions, // ✅ stringify the object
          businessId: this.form.get('businessId')?.value
        })
      )
    }
    this.blockPermissionSetId = ''
  }

  isChecked(permissions: string[], action: string): boolean {
    return permissions.includes(action)
  }

  toggleIsStandard() {
    this.isStandard = !this.isStandard
    // if (this.modalTitle === 'Create Role' || this.modalTitle === 'Edit Role') {
    //   this.form.get('isStandard')?.setValue(this.isStandard)
    // } else {
    //   this.form.get('isStandard')?.setValue(false)
    // }
  }

  getTransformedPermissions(): Record<string, PermissionAction[]> {
    const result: Record<string, PermissionAction[]> = {}

    Object.entries(this.permissionsState).forEach(([moduleName, actions]) => {
      const allowedActions = Object.entries(actions)
        .filter(([_, isAllowed]) => isAllowed) // filter true permissions
        .map(([action]) => action as PermissionAction) // cast to proper type

      if (allowedActions.length > 0) {
        const camelCaseName = moduleName
        result[camelCaseName] = allowedActions
      }
    })

    return result
  }

  private toCamelCase(name: string): string {
    return name
      .replace(/[^a-zA-Z0-9 ]/g, '') // remove special characters
      .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
        index === 0 ? match.toLowerCase() : match.toUpperCase()
      )
      .replace(/\s+/g, '')
  }

  setPermissionsState(permissions: Record<string, string[]>) {
    Object.entries(permissions).forEach(([module, actions]) => {
      this.permissionsState[module] = {
        view: actions.includes('view'),
        add: actions.includes('add'),
        edit: actions.includes('edit'),
        delete: actions.includes('delete'),
        status: actions.includes('status')
      }
    })
  }

  // Returns whether all checkboxes for an action are checked
  // isAllChecked(action: 'view' | 'add' | 'edit' | 'delete'): boolean {
  //   // return this.permissionModules.every(module => this.permissionsState[module.key]?.[action])
  //   return this.permissionModules.every(
  //     module =>
  //       this.permissionsState[module.key] !== undefined &&
  //       this.permissionsState[module.key][action] === true
  //   )
  // }

  isAllChecked(action: 'view' | 'add' | 'edit' | 'delete'): boolean {
    return (
      Array.isArray(this.permissionModules) &&
      this.permissionModules.length > 0 &&
      this.permissionModules.every(module => !!this.permissionsState[module.key]?.[action])
    )
  }

  // Toggles all checkboxes for a column based on the header checkbox
  toggleAllColumn(action: 'view' | 'add' | 'edit' | 'delete', checked: boolean): void {
    for (const module of this.permissionModules) {
      if (!this.permissionsState[module.key]) {
        this.permissionsState[module.key] = {}
      }

      // Always check 'view' if action is add/edit/delete
      if (['add', 'edit', 'delete'].includes(action) && checked) {
        this.permissionsState[module.key]['view'] = true
      }

      this.permissionsState[module.key][action] = checked
    }
  }

  resetPermissions() {
    this.permissionsState = {}

    // Optional: reset all permissions to false
    this.permissionModules.forEach(module => {
      this.permissionsState[module.key] = {}
      module.permissions.forEach(action => {
        // this.permissionsState[module.key][action] = false
      })
    })
  }

  get isSystemRole(): boolean {
    const name = this.form.get('name')?.value
    // console.log('name', this.systemRoles.includes(name))
    return this.systemRoles.includes(name)
  }

  customSearchFn = (term: string, item: any) => {
    const fullName = `${item.businessName}`.toLowerCase()
    return fullName.includes(term.toLowerCase())
  }

  isAdmin(): boolean {
    return this.utilsService.isAdmin()
  }

  openBusinessModal(content: TemplateRef<any>, options: any = {}, title: string) {
    this.modalTitle = title
    this.modalRef = this.modalService.open(content, {
      scrollable: false,
      centered: true,
      size: 'lg',
      ...options
    })

    // Pre-fill form if in edit mode
    if (options) {
      this.blockPermissionSetId = options.id
      //   this.setPermissionsState(options.permissions)
      //   this.setPermissions(options.PermissionSetPermissions)
      this.setPermissionsModule(this.modules, options.permissionSetPermissions)

      // this.modalTitle = `Edit PermissionSet`
      this.modalTitle = title ?? 'Edit PermissionSet'

      this.form.patchValue({
        name: options.name,
        isStandard: options.isStandard,
        businessId: options.businessId,
        permissions: options.permissions // assuming you have logic to map this
      })
    } else {
      this.form.reset() // if you're creating a new one
    }
  }

  getTransformedPermissionsForApi(): ApiPermissionPayload[] {
    // console.log(
    //   'All module names:',
    //   this.modules.map(m => m.name)
    // )

    const result: ApiPermissionPayload[] = []

    Object.entries(this.permissionsState).forEach(([moduleKey, actions]) => {
      const module =
        this.modules.find(m => m.name.toLowerCase() === moduleKey) ||
        this.modules.find(m => m.id === moduleKey)
      // console.log('get transformed module', moduleKey)
      if (!module) return

      const allowedPermissionIds = Object.entries(actions)
        .filter(([_, isAllowed]) => isAllowed)
        .map(([action]) => {
          const permission = module.permissions.find(p => p.action === action)
          return permission ? permission.id : null
        })
        .filter(id => id !== null) as string[]

      if (allowedPermissionIds.length > 0) {
        result.push({
          moduleId: module.id,
          permissionIds: allowedPermissionIds
        })
      }
    })

    return result
  }

  setPermissions(permissionSetPermissions: any) {
    // if (!PermissionSet || !PermissionSet.PermissionSetPermissions) {
    //   console.error('Invalid PermissionSet object passed to setPermissions:', PermissionSet)
    //   return
    // }

    this.permissionsState = {}
    this.permissionModules = []

    permissionSetPermissions.forEach((rp: PermissionSetPermission) => {
      const moduleName = rp.permission.module.name.toLowerCase() // e.g., "dashboard"
      const action = rp.permission.action as PermissionAction // e.g., "view"

      // Initialize permissionsState for the module
      if (!this.permissionsState[moduleName]) {
        this.permissionsState[moduleName] = {}
        this.permissionModules.push({
          key: moduleName,
          name: rp.permission.module.name,
          permissions: ['view', 'add', 'edit', 'delete'] // all possible actions
        })
      }

      // Mark this action as true
      this.permissionsState[moduleName][action] = true
    })

    // console.log('Final permissionsState:', this.permissionsState)
  }

  setPermissionsModule(modules: any, permissionSetPermissions: any) {
    // if (!PermissionSet || !PermissionSet.rolePermissions) {
    //   console.error('Invalid role object passed to setPermissions:', role)
    //   return
    // }

    this.permissionsState = {}
    this.permissionModules = []

    // 1️⃣ Step 1: Initialize all modules from response.modules
    modules.forEach((mod: any) => {
      const moduleKey = mod.name.toLowerCase()

      // Create a default false state for each action
      this.permissionsState[moduleKey] = {
        view: false,
        add: false,
        edit: false,
        delete: false
      }

      // Add to permissionModules array for rendering in template
      this.permissionModules.push({
        key: moduleKey,
        name: mod.name,
        permissions: ['view', 'add', 'edit', 'delete']
      })
    })

    // 2️⃣ Step 2: Update permissionsState based on PermissionSetPermissions
    permissionSetPermissions.forEach((rp: any) => {
      const moduleKey = rp.permission.module.name.toLowerCase()
      const action = rp.permission.action as PermissionAction

      if (this.permissionsState[moduleKey]) {
        this.permissionsState[moduleKey][action] = true
      }
    })

    // console.log('Final permissionsState:', this.permissionsState)
  }

  checkAssigned(roleId: string) {
    if (this.assignedMap[roleId] !== undefined) return // prevent duplicate calls
    this.permissionSetService.getAssignedTo(roleId).subscribe({
      next: res => {
        this.assignedMap[roleId] = res.totalCount > 0
      },
      error: () => {
        this.assignedMap[roleId] = false
      }
    })
  }

  openDeleteModal(employeeId: string) {
    const modalRef = this.modalService.open(DeleteComponent)
    modalRef.componentInstance.recordId = employeeId
    modalRef.result.then(
      result => {
        // Handle the result from the modal
        if (result) {
          // Call the service to update the business status
          this.handleDeleteConfirm(result)
        }
      },
      reason => {
        // Handle the dismiss case
        if (reason) {
          this.handleDeleteConfirm(reason)
        }
      }
    )
  }

  handleDeleteConfirm(data: { id: string }) {
    this.permissionSetService.deletePermissionSet(data.id).subscribe({
      next: () => {
        this.loadPermissionSets()
      },
      error: (error: { message: string }) => {
        this.toastr.error('Error: ' + error.message)
      }
    })
  }
}
